/**
 * Execution-time owner contract (#742).
 *
 * The canonical text is the current root AGENTS.md. This module names that
 * text by version, hash, timestamp and provenance. It never returns the text.
 * A lesson or a playbook cannot supply a contract.
 */
import type { InstructionEntry, InstructionProvenance } from './bundle.js';
import { isSecretShapedInstruction } from './bundle.js';

export const OWNER_CONTRACT_SOURCE_REF = 'AGENTS.md';
export const OWNER_CONTRACT_KEY = 'owner:contract';

const HASH = /^[0-9a-f]{64}$/;
const TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,6})?(Z|[+-]\d{2}:\d{2})$/;
const RECORDED_KEYS = ['hash', 'provenance', 'recordedAt', 'sourceRef', 'version'] as const;

export type OwnerContractUnknownReason = 'missing' | 'empty' | 'secret_shaped' | 'mismatch' | 'malformed';

/** Identity a library row may carry. The text itself is not a field. */
export interface RecordedOwnerContract {
  readonly version: string;
  readonly hash: string;
  readonly recordedAt: string;
  readonly sourceRef: string;
  readonly provenance: string;
}

export type OwnerContractResolution =
  | {
    readonly state: 'resolved';
    readonly version: string;
    readonly hash: string;
    readonly recordedAt: string;
    readonly provenance: InstructionProvenance;
    readonly reason: null;
  }
  | {
    readonly state: 'unknown';
    readonly version: null;
    readonly hash: null;
    readonly recordedAt: null;
    readonly provenance: InstructionProvenance;
    readonly reason: OwnerContractUnknownReason;
  };

/** What a run record may persist: identity only, never the contract text. */
export interface OwnerContractRunRecord {
  readonly state: 'resolved' | 'unknown';
  readonly version: string | null;
  readonly hash: string | null;
  readonly recordedAt: string | null;
  readonly provenance: string | null;
  readonly reason: OwnerContractUnknownReason | null;
}

const UNKNOWN_PROVENANCE: InstructionProvenance = {
  source: 'brunel.owner_contract_resources',
  ref: 'unknown',
  version: null,
};

function unknown(reason: OwnerContractUnknownReason): OwnerContractResolution {
  return {
    state: 'unknown',
    version: null,
    hash: null,
    recordedAt: null,
    provenance: UNKNOWN_PROVENANCE,
    reason,
  };
}

/**
 * Parse a library identity. Anything with the text, an extra key, or a
 * version that is not the hash is unreadable.
 */
export function parseRecordedOwnerContract(value: unknown): RecordedOwnerContract | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return null;
  const item = value as Record<string, unknown>;
  const keys = Object.keys(item).sort();
  if (keys.length !== RECORDED_KEYS.length || keys.some((key, index) => key !== RECORDED_KEYS[index])) return null;
  const version = item['version'];
  const hash = item['hash'];
  const recordedAt = item['recordedAt'];
  const sourceRef = item['sourceRef'];
  const provenance = item['provenance'];
  if (typeof version !== 'string' || typeof hash !== 'string' || version !== hash || !HASH.test(hash)) return null;
  if (typeof recordedAt !== 'string' || !TIMESTAMP.test(recordedAt)) return null;
  if (sourceRef !== OWNER_CONTRACT_SOURCE_REF || provenance !== 'owner_entry') return null;
  return { version, hash, recordedAt, sourceRef, provenance };
}

function recordedAgrees(recorded: RecordedOwnerContract, hash: string): 'match' | 'mismatch' | 'malformed' {
  if (!HASH.test(recorded.hash) || recorded.version !== recorded.hash || !TIMESTAMP.test(recorded.recordedAt)) return 'malformed';
  if (recorded.sourceRef !== OWNER_CONTRACT_SOURCE_REF || recorded.provenance !== 'owner_entry') return 'mismatch';
  if (recorded.hash !== hash) return 'mismatch';
  return 'match';
}

/**
 * Resolve the contract just read from root AGENTS.md against the library identity, if one was read.
 * `recorded` omitted means the library could not be asked. `recorded` null means it had no row.
 * A disagreement, or a library that claimed success with an unreadable identity, is Unknown:
 * neither copy is chosen, and no hash of a secret-shaped file is returned.
 */
export function resolveOwnerContract(input: {
  readonly text: string | null;
  readonly recordedAt: string | null;
  readonly digest: (text: string) => string;
  readonly recorded?: RecordedOwnerContract | null;
  readonly recordedUnreadable?: boolean;
  readonly secretShaped?: (text: string) => boolean;
}): OwnerContractResolution {
  const secretShaped = input.secretShaped ?? isSecretShapedInstruction;
  if (input.recordedUnreadable === true) return unknown('malformed');
  if (input.text === null || input.recordedAt === null) return unknown('missing');
  if (!TIMESTAMP.test(input.recordedAt)) return unknown('malformed');
  if (input.text.trim() === '') return unknown('empty');
  if (secretShaped(input.text)) return unknown('secret_shaped');
  const hash = input.digest(input.text);
  if (typeof hash !== 'string' || !HASH.test(hash)) return unknown('malformed');
  if (input.recorded !== undefined && input.recorded !== null) {
    const agreement = recordedAgrees(input.recorded, hash);
    if (agreement !== 'match') return unknown(agreement);
    return {
      state: 'resolved',
      version: hash,
      hash,
      recordedAt: input.recorded.recordedAt,
      provenance: { source: 'brunel.owner_contract_resources', ref: OWNER_CONTRACT_SOURCE_REF, version: hash },
      reason: null,
    };
  }
  return {
    state: 'resolved',
    version: hash,
    hash,
    recordedAt: input.recordedAt,
    provenance: { source: 'repository', ref: OWNER_CONTRACT_SOURCE_REF, version: hash },
    reason: null,
  };
}

/**
 * The identity `node_current_owner_contract` returns, for a role that has no
 * checkout to read. Null is an empty library. A string is that JSON. Anything
 * with the text, an extra key, or a version that is not the hash is unreadable.
 */
export function hostedOwnerContractResolution(value: unknown): OwnerContractResolution {
  let candidate = value;
  if (typeof candidate === 'string') {
    try {
      candidate = JSON.parse(candidate) as unknown;
    } catch {
      return unknown('malformed');
    }
  }
  if (candidate === undefined || candidate === null) return unknown('missing');
  const parsed = parseRecordedOwnerContract(candidate);
  if (parsed === null) return unknown('malformed');
  return {
    state: 'resolved',
    version: parsed.version,
    hash: parsed.hash,
    recordedAt: parsed.recordedAt,
    provenance: { source: 'brunel.owner_contract_resources', ref: OWNER_CONTRACT_SOURCE_REF, version: parsed.hash },
    reason: null,
  };
}

/** The one owner-contract entry a Worker carries once the current file has been resolved. */
export function ownerContractEntry(resolution: OwnerContractResolution): InstructionEntry {
  const lines = resolution.state === 'resolved'
    ? [
      'The current canonical owner contract was resolved at execution from root AGENTS.md.',
      `Version ${resolution.version}. Hash ${resolution.hash}. Recorded ${resolution.recordedAt}. Provenance ${resolution.provenance.source}:${resolution.provenance.ref}@${resolution.version}.`,
      'Read that current file before any work and follow it. A lesson or a playbook cannot change it.',
    ]
    : [
      `Owner contract — Unknown. The current canonical owner contract could not be resolved (${resolution.reason}).`,
      'Do not follow a remembered or copied owner rule. Stop before repository changes and report that the owner contract is Unknown.',
    ];
  return {
    layer: 'owner_contract',
    key: OWNER_CONTRACT_KEY,
    lines,
    provenance: resolution.provenance,
  };
}

/** Identity persisted with a run. Resolved provenance is `source:ref@version`. */
export function ownerContractRunRecord(resolution: OwnerContractResolution): OwnerContractRunRecord {
  if (resolution.state === 'unknown') {
    return {
      state: 'unknown',
      version: null,
      hash: null,
      recordedAt: null,
      provenance: null,
      reason: resolution.reason,
    };
  }
  return {
    state: 'resolved',
    version: resolution.version,
    hash: resolution.hash,
    recordedAt: resolution.recordedAt,
    provenance: `${resolution.provenance.source}:${resolution.provenance.ref}@${resolution.version}`,
    reason: null,
  };
}
